/**
 * The fixed choices a person makes on an alert (ops insights Phase 7 spec §6): how long to snooze
 * it, how long to mute its subject, and the limits on what they type. Pure, so the server
 * decides the instant and a screen can say it the same way.
 *
 * Every "until" is read on the SHOP's clock: "tomorrow 7 AM" is the shop's 7 AM, whatever the
 * browser's zone, and across a clock change it is still 7 AM.
 */
import { can, type Role } from './constants/roles';
import { DELIVERY_FAILURES } from './delivery';
import { MONITOR_CATALOG, type AlertSubjectType, type MonitorKey } from './monitors';
import {
  addDaysYmd,
  localDateOf,
  localWallTimeUtc,
  shiftLocalDays,
} from './mcp/timezone';

/** The morning hour a snooze (and a dated mute) ends at, shop time. */
export const ALERT_MORNING_HOUR = 7;

/** "Resolve…" needs a note (spec §3), at most this long (a CHECK holds the same number). */
export const ALERT_RESOLUTION_NOTE_MAX = 500;
/** "Mute…" needs a reason everyone can see (story 6), at most this long (a CHECK too). */
export const ALERT_MUTE_REASON_MAX = 300;

/**
 * The testing review (spec §7): what a monitor in Testing raised in this many days, for the
 * owner to judge before switching it Live.
 */
export const ALERT_REVIEW_DAYS = 7;

/** A snooze set to a date ends at most this many days ahead. */
export const ALERT_SNOOZE_MAX_DAYS = 30;
/** A mute set to a date ends at most this many days ahead. */
export const ALERT_MUTE_MAX_DAYS = 365;

/**
 * `tomorrow`: tomorrow 7 AM. `three_days`: the same clock time three days on. `monday`: the next
 * Monday 7 AM (a Monday's "Monday" is a week later). `date`: 7 AM on a chosen date, from
 * tomorrow to 30 days ahead.
 */
export const ALERT_SNOOZE_CHOICES = ['tomorrow', 'three_days', 'monday', 'date'] as const;
export type AlertSnoozeChoice = (typeof ALERT_SNOOZE_CHOICES)[number];

/** `thirty_days` and `ninety_days` from now (same clock time); `date`: 7 AM on a chosen date. */
export const ALERT_MUTE_CHOICES = ['thirty_days', 'ninety_days', 'date'] as const;
export type AlertMuteChoice = (typeof ALERT_MUTE_CHOICES)[number];

/** Why a chosen date was refused, in words a person reads on the form. */
export class AlertDateRefused extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AlertDateRefused';
  }
}

const morningOf = (ymd: string, timeZone: string): Date =>
  localWallTimeUtc(ymd, ALERT_MORNING_HOUR * 60, timeZone);

/** 7 AM on `date`, which must fall from tomorrow to `maxDays` days ahead (shop calendar). */
function morningOnChosenDate(
  date: string | undefined,
  now: Date,
  timeZone: string,
  maxDays: number,
): Date {
  if (!date) throw new AlertDateRefused('Choose a date.');
  const today = localDateOf(now, timeZone);
  if (date <= today) throw new AlertDateRefused('Choose a date after today.');
  if (date > addDaysYmd(today, maxDays)) {
    throw new AlertDateRefused(`Choose a date at most ${maxDays} days ahead.`);
  }
  return morningOf(date, timeZone);
}

/** When a snooze chosen at `now` ends. */
export function snoozeUntil(
  choice: AlertSnoozeChoice,
  now: Date,
  timeZone: string,
  date?: string,
): Date {
  const today = localDateOf(now, timeZone);
  switch (choice) {
    case 'tomorrow':
      return morningOf(addDaysYmd(today, 1), timeZone);
    case 'three_days':
      return shiftLocalDays(now, 3, timeZone);
    case 'monday': {
      const [y, m, d] = today.split('-').map(Number);
      const weekday = new Date(Date.UTC(y!, m! - 1, d!, 12)).getUTCDay();
      // Days to the NEXT Monday: 1 from a Sunday, 7 from a Monday.
      return morningOf(addDaysYmd(today, (8 - weekday) % 7 || 7), timeZone);
    }
    case 'date':
      return morningOnChosenDate(date, now, timeZone, ALERT_SNOOZE_MAX_DAYS);
  }
}

/** When a mute chosen at `now` ends. */
export function muteUntil(
  choice: AlertMuteChoice,
  now: Date,
  timeZone: string,
  date?: string,
): Date {
  switch (choice) {
    case 'thirty_days':
      return shiftLocalDays(now, 30, timeZone);
    case 'ninety_days':
      return shiftLocalDays(now, 90, timeZone);
    case 'date':
      return morningOnChosenDate(date, now, timeZone, ALERT_MUTE_MAX_DAYS);
  }
}

// ── The morning email's ledger (`alert_digests`, spec §8; ticket 06 sends it) ────────────────

/** Where one person's morning email for one shop day stands (the `ticket_emails` pattern). */
export const ALERT_DIGEST_STATUSES = [
  'queued',
  'sending',
  'sent',
  'skipped',
  'failed',
] as const;
export type AlertDigestStatus = (typeof ALERT_DIGEST_STATUSES)[number];

/**
 * Why a `skipped` or `failed` digest did not go: every `DELIVERY_FAILURES` reason, plus
 *  - `interrupted`: a worker died between claiming the row and hearing back (it MAY have gone,
 *    so it is never sent again), or the email could not be built after the claim;
 *  - `nothing-open`: the board had nothing open when the email was built, so none was sent
 *    (spec §8: "No email when nothing is open"). Recorded, so a quiet morning is a row that
 *    says so, never a missing one.
 *
 * Since MAP 249 (post-merge fix-up r1 #2) the send gate's own first steps, in these words, all
 * `skipped`: an address its person never confirmed → `no-recipient`; the shop paused or held by
 * its name → `rejected` (ServWell said no, as a staff reject is); ServWell's daily ceiling
 * reached → `throttled`, for the day.
 *
 * ⚠️ A second copy lives in the `alert_digests_failure_check` CHECK (migration 0043).
 */
export const ALERT_DIGEST_FAILURES = [
  ...DELIVERY_FAILURES,
  'interrupted',
  'nothing-open',
] as const;
export type AlertDigestFailure = (typeof ALERT_DIGEST_FAILURES)[number];

/**
 * The morning email goes out after the shop's hourly check at this hour, shop time (spec §8):
 * the same morning hour a "tomorrow" snooze ends at, so an alert snoozed until this morning is
 * in it.
 */
export const ALERT_DIGEST_HOUR = ALERT_MORNING_HOUR;

/** The email lists at most this many alerts, then "+N more in the app". */
export const ALERT_DIGEST_MAX_LINES = 10;

/**
 * Who may get the morning email, and so who has the "Morning alerts email" switch: whoever reads
 * the board (`alert:read`: the owner and the office; never a tech or the bookkeeper). One rule
 * for the switch, the queue and the send, asked of the role table at call time.
 */
export function mayGetAlertDigest(role: Role): boolean {
  return can(role, 'alert', 'read');
}

/** An alert's projected evidence (the catalog's allow-list): plain values only. */
export type AlertEvidence = Readonly<Record<string, string | number | boolean | null>>;

const evidenceText = (v: unknown): string | null =>
  v === null || v === undefined || v === '' ? null : String(v);
const numbered = (v: unknown): string | null =>
  evidenceText(v) ? `#${evidenceText(v)}` : null;
const joinedText = (parts: (string | null)[], sep: string): string =>
  parts.filter((p): p is string => p !== null).join(sep);

/**
 * What an alert is about, in one line, by its subject: "#42 Leaking valve", "Capacitor 45/5 ·
 * Van 1", "Quote #118 · Rivera Home". The board's card and the morning email both say it this
 * way (one definition, so the page and the email never drift). Built from the allow-listed
 * evidence only; when it says nothing usable the monitor's own name stands (never blank).
 */
export function alertSubjectLine(
  a: {
    monitorKey: MonitorKey;
    subject: { type: AlertSubjectType };
    evidence: AlertEvidence;
  },
  label: string = MONITOR_CATALOG[a.monitorKey].label,
): string {
  const e = a.evidence;
  let line = '';
  switch (a.subject.type) {
    case 'ticket':
      line = joinedText([numbered(e.number), evidenceText(e.title)], ' ');
      break;
    case 'stock':
      line = joinedText([evidenceText(e.item), evidenceText(e.location)], ' · ');
      break;
    case 'agreement':
      line = joinedText(
        [
          evidenceText(e.planName),
          e.planNumber != null ? `Plan ${numbered(e.planNumber)}` : null,
        ],
        ' · ',
      );
      break;
    case 'request':
      line = e.number != null ? `Request ${numbered(e.number)}` : '';
      break;
    case 'quote':
      line = joinedText(
        [
          e.number != null ? `Quote ${numbered(e.number)}` : null,
          evidenceText(e.customer),
        ],
        ' · ',
      );
      break;
    case 'customer':
      line = evidenceText(e.customer) ?? '';
      break;
    case 'job':
      line = e.number != null ? `Job ${numbered(e.number)}` : '';
      break;
  }
  return line || label;
}
